import React, { useCallback, useEffect, useState } from "react";
import api, { fileUrl , formatTanggal} from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import BackupExcelButton from "@/components/BackupExcelButton";
import { FileText, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const F_EMPTY = { q: "", jenis_izin: "", bulan: "", tahun: "" };
const JENIS_OPTS = [
  { v: "terlambat", label: "Terlambat" }, { v: "alfa", label: "Izin" },
  { v: "sakit", label: "Sakit" }, { v: "dinas_luar", label: "Dinas Luar" },
  { v: "pulang_cepat", label: "Pulang Cepat" },
];
const JENIS_LABEL = {
  terlambat: "Terlambat", alfa: "Izin", sakit: "Sakit",
  dinas_luar: "Dinas Luar", pulang_cepat: "Pulang Cepat",
};
const KEPADA_OPTS = ["Kepala Sekolah", "Ketua Yayasan"];
const MELALUI_OPTS = ["Lisan", "Pesan", "Telepon"];

export function ListIzin() {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => { try { const { data } = await api.get("/izin-guru"); setAll(data); } catch (e) { console.error(e); } }, []);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((it) => {
    if (applied.q && !(it.nama_guru || "").toLowerCase().includes(applied.q.toLowerCase())) return false;
    if (applied.jenis_izin && it.jenis_izin !== applied.jenis_izin) return false;
    if (applied.bulan && (it.tanggal || "").slice(5, 7) !== applied.bulan) return false;
    if (applied.tahun && (it.tanggal || "").slice(0, 4) !== String(applied.tahun)) return false;
    return true;
  });

  const del = async (id) => {
    if (!window.confirm("Hapus data izin ini?")) return;
    try { await api.delete(`/izin-guru/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/izin-guru/${editItem.id}`, {
        jenis_izin: editItem.jenis_izin,
        tanggal: editItem.tanggal,
        alasan: editItem.alasan || "",
        kepada: editItem.kepada || null,
        melalui: editItem.melalui || null,
        bukti_file_id: editItem.bukti_file_id || null,
      });
      toast.success("Izin diperbarui");
      setEditItem(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Daftar Izin Guru" action={<BackupExcelButton endpoint="izin-guru" filters={applied} testId="backup-izin-btn" />} />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="izin-filter"
        fields={[
          { key: "q", type: "text", label: "Nama Guru", placeholder: "Cari nama guru" },
          { key: "jenis_izin", type: "select", label: "Jenis Izin", options: JENIS_OPTS },
          { key: "bulan", type: "bulan", label: "Bulan" },
          { key: "tahun", type: "tahun", label: "Tahun" },
        ]}
      />
      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full text-sm">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Tanggal</th>
              <th className="text-left px-5 py-3">Nama Guru</th>
              <th className="text-left px-5 py-3">Jenis Izin</th>
              <th className="text-left px-5 py-3">Kepada</th>
              <th className="text-left px-5 py-3">Melalui</th>
              <th className="text-left px-5 py-3">Alasan</th>
              <th className="text-left px-5 py-3">Bukti</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400 italic">Belum ada izin.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} className="pp-row">
                  <td className="px-5 py-3 font-mono text-xs">{formatTanggal(it.tanggal)}</td>
                  <td className="px-5 py-3 font-medium">{it.nama_guru}</td>
                  <td className="px-5 py-3"><span className="pp-badge-info">{JENIS_LABEL[it.jenis_izin] || it.jenis_izin}</span></td>
                  <td className="px-5 py-3 text-sm">{it.jenis_izin === "pulang_cepat" ? (it.kepada || "—") : "—"}</td>
                  <td className="px-5 py-3 text-sm">{it.jenis_izin === "pulang_cepat" ? (it.melalui || "—") : "—"}</td>
                  <td className="px-5 py-3 text-sm">{it.alasan || "—"}</td>
                  <td className="px-5 py-3">{it.bukti_file_id ? <a href={fileUrl(it.bukti_file_id)} target="_blank" rel="noreferrer" className="text-indigo-600 inline-flex items-center gap-1 text-xs"><FileText className="w-3.5 h-3.5" />Lihat</a> : "—"}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditItem({ ...it })} className="pp-btn-ghost text-xs mr-1" data-testid={`izin-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
                    <button onClick={() => del(it.id)} className="pp-btn-danger" data-testid={`izin-del-${it.id}`}><Trash2 className="w-3.5 h-3.5" />Hapus</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="izin-edit-modal">
          <div className="pp-card p-6 w-full max-w-lg animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Edit Izin — {editItem.nama_guru}</h3>
              <button onClick={() => setEditItem(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={saveEdit} className="space-y-3">
              <div>
                <label className="pp-label">Jenis Izin</label>
                <select className="pp-input" value={editItem.jenis_izin} onChange={(e) => setEditItem({ ...editItem, jenis_izin: e.target.value })} data-testid="izin-edit-jenis">
                  {JENIS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="pp-label">Tanggal</label>
                <input type="date" className="pp-input" value={editItem.tanggal} onChange={(e) => setEditItem({ ...editItem, tanggal: e.target.value })} data-testid="izin-edit-tanggal" />
              </div>
              {editItem.jenis_izin === "pulang_cepat" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="pp-label">Kepada</label>
                    <select className="pp-input" value={editItem.kepada || ""} onChange={(e) => setEditItem({ ...editItem, kepada: e.target.value })}>
                      <option value="">—</option>
                      {KEPADA_OPTS.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="pp-label">Melalui</label>
                    <select className="pp-input" value={editItem.melalui || ""} onChange={(e) => setEditItem({ ...editItem, melalui: e.target.value })}>
                      <option value="">—</option>
                      {MELALUI_OPTS.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
              )}
              <div>
                <label className="pp-label">Alasan</label>
                <textarea className="pp-input" rows={3} value={editItem.alasan || ""} onChange={(e) => setEditItem({ ...editItem, alasan: e.target.value })} data-testid="izin-edit-alasan" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditItem(null)} className="pp-btn-secondary">Batal</button>
                <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="izin-edit-save">{saving ? "Menyimpan…" : "Simpan"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
